import { capitalize } from "@mui/material";
import {
  BubbleText,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetAccountDetail } from "@/api/graphql/accounts/getAccountDocument/hooks.ts";
import { useGetNotificationsByAccount } from "@/api/graphql/notifications/account/hooks.ts";
import { getFullName } from "@/components/Notifications/account/helper.ts";
import { StyledEmailText } from "@/components/Notifications/account/styles.ts";
import { NOTIFICATION_PER_PAGE } from "@/components/Notifications/constants.ts";
import { getStatusAndColor } from "@/components/Notifications/userList/helper.ts";
import { StyledNoDataText } from "@/components/Notifications/userList/styles.ts";
import { StyledCellText } from "@/components/Users/list/styles.ts";
import { useFormattedDate } from "@/hooks/i18n/useFormatDate/hook.ts";
export const useNotificationTableData = (accountId: string) => {
  const { t: tNotifications } = useTranslation("notifications");
  const formattedDate = useFormattedDate();
  const {
    data,
    isLoading: isNotificationsLoading,
    totalNotifications,
    hasNextPage,
    fetchNextPage,
  } = useGetNotificationsByAccount({
    pageSize: NOTIFICATION_PER_PAGE,
    accountId,
  });
  const { companyData, isLoading: isAccountLoading } =
    useGetAccountDetail(accountId);
  const notifications = useMemo(
    () => data?.pages?.flatMap((current) => current?.data ?? []) ?? [],
    [data],
  );
  const columns = useMemo(
    () => [
      {
        columnName: tNotifications("notifications.table.users"),
        displayedByDefault: true,
        cellWidth: "20%",
      },
      {
        columnName: tNotifications("notifications.table.object"),
        displayedByDefault: true,
        cellWidth: "45%",
      },
      {
        columnName: tNotifications("notifications.table.attachment"),
        displayedByDefault: true,
        cellWidth: "10%",
      },
      {
        columnName: tNotifications("notifications.table.status"),
        displayedByDefault: true,
        cellWidth: "15%",
      },
      {
        columnName: tNotifications("notifications.table.dateSent"),
        displayedByDefault: true,
        cellWidth: "10%",
      },
    ],
    [tNotifications],
  );
  const noDataLabel = tNotifications("notifications.dataNotAvailable");
  const rows = useMemo(
    () =>
      notifications.map((notification) => {
        const { translatedStatus, color } = getStatusAndColor({
          status: notification.status,
          failedReason: notification.failedReason,
          tNotifications,
        });
        const getFullNameNormalized = getFullName({
          firstName: notification.firstName,
          lastName: notification.lastName,
          tNotifications,
        });

        const userInformations =
          getFullNameNormalized === noDataLabel ? (
            <StyledNoDataText variant={"text1"}>
              {getFullNameNormalized}
            </StyledNoDataText>
          ) : (
            <>
              <Text variant={"text2"}>{getFullNameNormalized}</Text>
              <StyledEmailText variant={"text1"}>
                {notification.email}
              </StyledEmailText>
            </>
          );

        return {
          data: {
            user: userInformations,
            subject: notification.subject,
            attachments: notification.hasAttachments
              ? tNotifications("notifications.attachments.with")
              : tNotifications("notifications.attachments.without"),
            status: (
              <StyledCellText variant='text2'>
                <BubbleText
                  text={capitalize(translatedStatus)}
                  variant={color}
                />
              </StyledCellText>
            ),
            dateSent: formattedDate(notification.createdTs),
          },
          key: notification.id,
        };
      }),
    [notifications, formattedDate, noDataLabel, tNotifications],
  );

  return {
    columns,
    rows,
    totalNotifications,
    isAccountLoading,
    isNotificationsLoading,
    companyData,
    hasNextPage,
    fetchNextPage,
  };
};
export const useBreadCrumbItems = (companyName?: string) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tNotifications } = useTranslation("notifications");

  return [
    {
      title: tCommon("common.features.dashboard"),
      path: "/" as const,
    },
    {
      title: companyName || "",
      path: "/account/$id" as const,
    },
    {
      title: tNotifications("notifications.title"),
      path: undefined,
    },
  ];
};
